import { useState } from "react";
import { Trash2, ChevronRight, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { JsonViewer } from "./JsonViewer";

export const HistoryPanel = ({ history, onClear, onSelect }) => {
  const [expandedId, setExpandedId] = useState(null);

  if (!history.length) {
    return (
      <div data-testid="history-empty" className="rounded-md border border-dashed border-zinc-800 p-6 text-center">
        <p className="text-sm text-zinc-500">No migrations yet in this session.</p>
        <p className="text-xs text-zinc-600 mt-1">Results will appear here after you run a migration.</p>
      </div>
    );
  }

  return (
    <div data-testid="history-panel" className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-zinc-500 font-mono">{history.length} migration(s) this session</span>
        <Button
          data-testid="clear-history-button"
          onClick={onClear}
          variant="outline"
          size="sm"
          className="h-8 border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-red-950/40 hover:text-red-400 hover:border-red-500/30"
        >
          <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Clear History
        </Button>
      </div>

      <div className="divide-y divide-zinc-800 rounded-md border border-zinc-800 overflow-hidden">
        {history.map((item) => {
          const isOpen = expandedId === item.id;
          return (
            <div key={item.id} data-testid="history-item">
              <button
                onClick={() => {
                  setExpandedId(isOpen ? null : item.id);
                  onSelect?.(item);
                }}
                className="flex w-full items-center gap-3 px-3 py-3 text-left hover:bg-zinc-800/50 transition-colors"
              >
                {item.success ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-400 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 font-mono text-xs text-zinc-300">
                    <span className="truncate">{item.source_waba_id}</span>
                    <ChevronRight className="h-3 w-3 text-zinc-600 shrink-0" />
                    <span className="truncate">{item.destination_waba_id}</span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-zinc-500 font-mono">
                    {new Date(item.request_timestamp).toLocaleString()}
                  </div>
                </div>
                <span
                  className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-mono font-semibold ${
                    item.success
                      ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                      : "bg-red-500/10 text-red-400 border-red-500/20"
                  }`}
                >
                  HTTP {item.http_status || "—"}
                </span>
              </button>
              {isOpen && (
                <div className="border-t border-zinc-800 bg-zinc-950/40 p-3">
                  <JsonViewer data={item.response_body} testId="history-response-viewer" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
